import {
  BoardRect,
  LunaCreation,
  LunaCreationKind,
  LunaCreationMode,
  LunaCreationPlacement,
  LunaCreationStatus,
} from 'src/app/models/luna-creation';
import type { WorkspaceTab } from 'src/app/services/luna-workspace/luna-workspace-tabs.util';

/** Prefix of a creating tab's id, so it can never collide with a board item's uid. */
export const CREATION_TAB_PREFIX = 'creation:';

const KINDS: readonly LunaCreationKind[] = ['visual', 'app'];
const STATUSES: readonly LunaCreationStatus[] = ['working', 'ready', 'failed', 'cancelled'];
const PLACEMENTS: readonly LunaCreationPlacement[] = ['view', 'tab', 'board'];
const MODES: readonly LunaCreationMode[] = ['board', 'tab'];

/** One line in the Luna panel about something Luna is making (see creationStatusLines). */
export interface CreationStatusLine {
  id: string;
  kind: LunaCreationKind;
  state: 'working' | 'ready' | 'failed';
  title: string;
  /** The app's tab, for "Open" on a ready app. */
  tab: WorkspaceTab | null;
}

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function asBoardRect(value: unknown): BoardRect | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const rect = value as Record<string, unknown>;
  const [x, y, width, height] = [rect.x, rect.y, rect.width, rect.height].map(asNumber);
  return x !== null && y !== null && width && height && width > 0 && height > 0
    ? { x, y, width, height }
    : null;
}

function parseOne(raw: unknown): LunaCreation | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const id = asString(item.id);
  const kind = KINDS.find((k) => k === item.kind);
  const status = STATUSES.find((s) => s === item.status);
  const title = asString(item.title);
  if (!id || !kind || !status || !title) {
    return null;
  }
  const steps = Array.isArray(item.steps)
    ? item.steps.filter((s): s is string => typeof s === 'string')
    : [];
  const step = Math.max(0, Math.min(asNumber(item.step) ?? 0, Math.max(steps.length - 1, 0)));
  return {
    id,
    kind,
    title,
    status,
    steps,
    step,
    label: asString(item.label) ?? steps[step] ?? '',
    startedAt: asNumber(item.started_at) ?? 0,
    etaS: asNumber(item.eta_s),
    itemUid: asString(item.item_uid),
    placement: PLACEMENTS.find((p) => p === item.placement) ?? null,
    mode: MODES.find((m) => m === item.mode) ?? null,
    spot: asBoardRect(item.spot),
  };
}

/**
 * The `luna.creations` attribute: `{v: 1, items: [...]}`. Bad or missing JSON, or an unknown
 * version, is an empty list, so a stale or broken attribute never leaves a tab behind.
 */
export function parseLunaCreations(raw: string | null | undefined): LunaCreation[] {
  if (!raw) {
    return [];
  }
  try {
    const data = JSON.parse(raw) as { v?: unknown; items?: unknown };
    if (data?.v !== 1 || !Array.isArray(data.items)) {
      return [];
    }
    return data.items.map(parseOne).filter((c): c is LunaCreation => c !== null);
  } catch {
    return [];
  }
}

/** Whole minutes left (0 = under a minute), or null when there is no estimate. */
export function creationMinutesLeft(creation: LunaCreation): number | null {
  if (creation.status !== 'working' || creation.etaS === null) {
    return null;
  }
  return creation.etaS < 60 ? 0 : Math.round(creation.etaS / 60);
}

/**
 * Tabs for apps still being made: one per app that is working, or ready but not yet on the
 * board as a tab. They sit after the real app tabs and cannot be opened. An app made for the
 * whiteboard gets none: its spot on the board shows it instead (see creationSpots).
 */
export function creatingTabsFor(
  creations: readonly LunaCreation[],
  appTabIds: readonly string[],
): WorkspaceTab[] {
  return creations
    .filter(
      (c) =>
        c.kind === 'app' &&
        c.mode !== 'board' &&
        (c.status === 'working' ||
          (c.status === 'ready' &&
            c.placement !== 'board' &&
            !appTabIds.includes(c.itemUid ?? ''))),
    )
    .map((creation) => ({
      kind: 'creating' as const,
      id: `${CREATION_TAB_PREFIX}${creation.id}`,
      label: creation.title,
      creation,
    }));
}

/** App tabs Luna made that the student has not opened yet: they carry a "new" dot. */
export function newCreationTabIds(
  creations: readonly LunaCreation[],
  appTabIds: readonly string[],
  seen: ReadonlySet<string>,
): string[] {
  return creations
    .filter((c) => c.kind === 'app' && c.status === 'ready' && c.placement === 'tab')
    .map((c) => c.itemUid ?? '')
    .filter((uid) => uid && appTabIds.includes(uid) && !seen.has(uid));
}

/**
 * The Luna panel's lines about what she is making: one per visual or app still being made, one
 * per app that arrived as a tab the student has not opened yet (with its tab, for Open), and a
 * line when the newest creation could not be made. At most three, newest last.
 */
export function creationStatusLines(
  creations: readonly LunaCreation[],
  tabs: readonly WorkspaceTab[],
  seen: ReadonlySet<string>,
): CreationStatusLine[] {
  const lines: CreationStatusLine[] = [];
  creations.forEach((c, i) => {
    const base = { id: c.id, kind: c.kind, title: c.title };
    if (c.status === 'working') {
      lines.push({ ...base, state: 'working', tab: null });
    } else if (c.status === 'ready' && c.kind === 'app' && c.placement === 'tab' && c.itemUid) {
      const tab = tabs.find((t) => t.id === c.itemUid);
      if (tab && !seen.has(c.itemUid)) {
        lines.push({ ...base, state: 'ready', tab });
      }
    } else if (c.status === 'failed' && i === creations.length - 1) {
      lines.push({ ...base, state: 'failed', tab: null });
    }
  });
  return lines.slice(-3);
}

/** A visual or app being made in a spot on the whiteboard (see LunaCreation.spot). */
export interface CreationSpot {
  id: string;
  kind: LunaCreationKind;
  title: string;
  spot: BoardRect;
}

/** The whiteboard spots held by things Luna is making and has not placed yet. */
export function creationSpots(creations: readonly LunaCreation[]): CreationSpot[] {
  return creations
    .filter((c) => (c.status === 'working' || c.status === 'ready') && !c.itemUid && c.spot)
    .map((c) => ({ id: c.id, kind: c.kind, title: c.title, spot: c.spot as BoardRect }));
}

/** A held spot as the student sees it: a rect on screen, relative to the whiteboard stage. */
export interface CreationSpotView extends CreationSpot {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where each held spot is on screen, given the canvas viewport transform (zoom, then pan) and
 * the stage the whiteboard is drawn in. Positions are relative to the stage's top left.
 */
export function creationSpotViews(
  spots: readonly CreationSpot[],
  vpt: readonly number[],
  stage: { left: number; top: number },
): CreationSpotView[] {
  const zoom = vpt[0];
  return spots.map((s) => ({
    ...s,
    left: s.spot.x * zoom + vpt[4] - stage.left,
    top: s.spot.y * zoom + vpt[5] - stage.top,
    width: s.spot.width * zoom,
    height: s.spot.height * zoom,
  }));
}
